import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { violationService } from '../services/apiService';
import { queryKeys } from '../utils/queryClient';
import { ErrorMessage } from '../components/ui/ErrorHandling';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  PageTitle, Badge, Button, Card, Select,
  EmptyState, SkeletonRows,
  Icons, Field, Input, Textarea,
  Drawer, DrawerBody,
} from '../shared/ui';

// ── badge helpers ─────────────────────────────────────────────────────────────
const SEVERITY_TONE = { low: 'warning', medium: 'amber', high: 'danger', critical: 'violet' };
const STATUS_TONE   = { reported: 'neutral', investigating: 'primary', confirmed: 'warning', resolved: 'success', dismissed: 'neutral' };

const SeverityBadge = ({ value }) => (
  <Badge tone={SEVERITY_TONE[value] || 'neutral'}>{value?.toUpperCase()}</Badge>
);
const StatusBadge = ({ value }) => (
  <Badge tone={STATUS_TONE[value] || 'neutral'}>{value?.toUpperCase()}</Badge>
);

// ── filter options ────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'all',          label: 'All Status' },
  { value: 'reported',     label: 'Reported' },
  { value: 'investigating',label: 'Investigating' },
  { value: 'confirmed',    label: 'Confirmed' },
  { value: 'resolved',     label: 'Resolved' },
  { value: 'dismissed',    label: 'Dismissed' },
];

const SEVERITY_OPTIONS = [
  { value: 'all',     label: 'All Severity' },
  { value: 'low',     label: 'Low' },
  { value: 'medium',  label: 'Medium' },
  { value: 'high',    label: 'High' },
  { value: 'critical',label: 'Critical' },
];

const EMPTY_FORM = {
  renter_id: '', room_id: '', violation_type: '', severity: 'low', description: '',
  occurred_at: new Date().toISOString().split('T')[0],
};

// ── component ─────────────────────────────────────────────────────────────────
const Violations = () => {
  const { t } = useTranslation(['violations', 'common']);
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [selectedViolation, setSelectedViolation] = useState(null);
  const [showDetailModal, setShowDetailModal]   = useState(false);
  const [showCreateModal, setShowCreateModal]   = useState(false);
  const [newComment, setNewComment] = useState('');
  const [viewingEvidence, setViewingEvidence]   = useState(null);
  const [statusFilter, setStatusFilter]         = useState('all');
  const [severityFilter, setSeverityFilter]     = useState('all');
  const [newViolation, setNewViolation]          = useState(EMPTY_FORM);

  // ── queries ──────────────────────────────────────────────────────────────────
  const { data: violations = [], isLoading, error } = useQuery({
    queryKey: ['violations', statusFilter, severityFilter],
    queryFn: () => violationService.getViolations({
      status:   statusFilter   !== 'all' ? statusFilter   : undefined,
      severity: severityFilter !== 'all' ? severityFilter : undefined,
    }),
  });

  const { data: violationDetail, refetch: refetchDetail } = useQuery({
    queryKey: ['violation-detail', selectedViolation?.uuid],
    queryFn: () => violationService.getViolation(selectedViolation.uuid),
    enabled: !!selectedViolation?.uuid,
  });

  const { data: comments = [], refetch: refetchComments } = useQuery({
    queryKey: ['violation-comments', selectedViolation?.uuid],
    queryFn: () => violationService.getViolationComments(selectedViolation.uuid),
    enabled: !!selectedViolation?.uuid,
  });

  // ── mutations ────────────────────────────────────────────────────────────────
  const createViolationMutation = useMutation({
    mutationFn: violationService.createViolation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['violations'] });
      setShowCreateModal(false);
      setNewViolation(EMPTY_FORM);
      toast.success(t('violations:toast.created'));
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to create violation'),
  });

  const updateViolationMutation = useMutation({
    mutationFn: ({ uuid, data }) => violationService.updateViolation(uuid, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['violations'] });
      queryClient.invalidateQueries({ queryKey: ['violation-detail', selectedViolation?.uuid] });
      toast.success(t('violations:toast.updated'));
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to update violation'),
  });

  const uploadEvidenceMutation = useMutation({
    mutationFn: ({ uuid, formData }) => violationService.uploadEvidence(uuid, formData),
    onSuccess: () => { refetchDetail(); toast.success(t('violations:toast.evidenceUploaded')); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to upload evidence'),
  });

  const deleteEvidenceMutation = useMutation({
    mutationFn: ({ uuid, evidenceId }) => violationService.deleteEvidence(uuid, evidenceId),
    onSuccess: () => { refetchDetail(); toast.success(t('violations:toast.evidenceDeleted')); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to delete evidence'),
  });

  const addCommentMutation = useMutation({
    mutationFn: ({ uuid, commentData }) => violationService.addViolationComment(uuid, commentData),
    onSuccess: () => { setNewComment(''); refetchComments(); toast.success(t('violations:toast.commentAdded')); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to add comment'),
  });

  // ── handlers ─────────────────────────────────────────────────────────────────
  const handleFileUpload = (e) => {
    Array.from(e.target.files).forEach((file) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('uploaded_by', localStorage.getItem('user_id') || '1');
      fd.append('description', `Evidence uploaded on ${new Date().toLocaleDateString()}`);
      uploadEvidenceMutation.mutate({ uuid: selectedViolation.uuid, formData: fd });
    });
  };

  const handleViewEvidence = async (evidence) => {
    try {
      const blob = await violationService.getEvidence(selectedViolation.uuid, evidence._id);
      const url = URL.createObjectURL(blob);
      setViewingEvidence({ ...evidence, url, type: evidence.content_type?.startsWith('video/') ? 'video' : 'image' });
    } catch {
      toast.error(t('violations:toast.evidenceLoadFailed'));
    }
  };

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <PageTitle
        title={t('violations:title')}
        subtitle={t('violations:subtitle')}
        actions={
          <Button
            kind="danger"
            size="sm"
            iconLeft={<Icons.Alert size={14} />}
            onClick={() => setShowCreateModal(true)}
          >
            {t('violations:action.reportViolation')}
          </Button>
        }
      />

      {/* Filters */}
      <Card padding={16}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-48">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={STATUS_OPTIONS}
            />
          </div>
          <div className="w-48">
            <Select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              options={SEVERITY_OPTIONS}
            />
          </div>
        </div>
      </Card>

      {/* Violations grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} padding={16}>
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-stone-100 rounded w-3/4" />
                <div className="h-3 bg-stone-100 rounded w-full" />
                <div className="h-3 bg-stone-100 rounded w-2/3" />
              </div>
            </Card>
          ))}
        </div>
      ) : error ? (
        <ErrorMessage
          error={error}
          onRetry={() => queryClient.invalidateQueries({ queryKey: ['violations'] })}
        />
      ) : violations.length === 0 ? (
        <Card padding={32}>
          <EmptyState
            icon={<Icons.Alert size={32} />}
            title="No violations found"
            description="All clear! No violations have been reported yet."
            primaryAction={
              <Button kind="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                Report Violation
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {violations.map((v) => (
            <Card
              key={v.uuid}
              padding={16}
              hoverable
              className="cursor-pointer transition-shadow hover:shadow-md"
              onClick={() => { setSelectedViolation(v); setShowDetailModal(true); }}
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-stone-900 text-sm leading-snug flex-1 mr-2">
                  {v.violation_type}
                </h3>
                <div className="flex flex-col gap-1 items-end flex-shrink-0">
                  <SeverityBadge value={v.severity} />
                  <StatusBadge   value={v.status} />
                </div>
              </div>

              <p className="text-sm text-stone-600 mb-3 line-clamp-2">{v.description}</p>

              <div className="flex items-center justify-between text-xs text-stone-500">
                <div className="flex items-center gap-1">
                  <Icons.Building size={12} />
                  Room {v.room_number || 'N/A'}
                </div>
                <div className="flex items-center gap-1">
                  <Icons.Calendar size={12} />
                  {format(new Date(v.occurred_at), 'MMM dd, yyyy')}
                </div>
              </div>

              {v.evidence_count > 0 && (
                <div className="mt-3 pt-3 border-t border-stone-100 flex items-center gap-1 text-xs text-teal-600">
                  <Icons.Camera size={12} />
                  {v.evidence_count} evidence file(s)
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* ── Detail Drawer ───────────────────────────────────────────────────────── */}
      <Drawer
        open={!!(showDetailModal && selectedViolation)}
        onClose={() => { setShowDetailModal(false); setSelectedViolation(null); setViewingEvidence(null); }}
        title={selectedViolation ? `#${selectedViolation.id}` : ''}
        subtitle={selectedViolation?.description}
        width={520}
      >
        <DrawerBody>
        {selectedViolation && (
          <div className="p-5">
              {/* Violation info */}
              <div className="mb-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-base font-semibold text-stone-900">{violationDetail?.violation_type}</h3>
                    <p className="text-sm text-stone-600 mt-1">{violationDetail?.description}</p>
                  </div>
                  <div className="flex flex-col gap-1.5 items-end ml-4">
                    <SeverityBadge value={violationDetail?.severity || selectedViolation.severity} />
                    <StatusBadge   value={violationDetail?.status   || selectedViolation.status} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    ['Room',    violationDetail?.room_number   || 'N/A'],
                    ['Renter',  violationDetail?.renter_name   || 'N/A'],
                    ['Occurred', format(new Date(violationDetail?.occurred_at || selectedViolation.occurred_at), 'MMM dd, yyyy')],
                    ['Reported', format(new Date(violationDetail?.created_at  || selectedViolation.created_at),  'MMM dd, yyyy')],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <span className="text-stone-500">{k}:</span>
                      <span className="ml-2 font-medium text-stone-900">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status update */}
              <div className="mb-6 p-4 bg-stone-50 rounded-xl border border-stone-100">
                <h4 className="text-sm font-semibold text-stone-700 mb-2">Update Status</h4>
                <select
                  value={violationDetail?.status || selectedViolation.status}
                  onChange={(e) => updateViolationMutation.mutate({ uuid: selectedViolation.uuid, data: { status: e.target.value } })}
                  className="w-full h-9 px-3 text-sm border border-stone-300 rounded-lg bg-white focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
                >
                  {STATUS_OPTIONS.filter(o => o.value !== 'all').map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* Evidence */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-semibold text-stone-700">
                    Evidence ({violationDetail?.evidence?.length || 0})
                  </h4>
                  <Button kind="secondary" size="sm" iconLeft={<Icons.Upload size={13} />} onClick={() => fileInputRef.current?.click()}>
                    Upload Evidence
                  </Button>
                  <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" onChange={handleFileUpload} className="hidden" />
                </div>

                {violationDetail?.evidence?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {violationDetail.evidence.map((ev) => (
                      <div key={ev._id} className="relative group">
                        <div className="aspect-video bg-stone-100 rounded-lg overflow-hidden flex items-center justify-center">
                          {ev.content_type?.startsWith('video/') ? (
                            <Icons.Video size={36} className="text-stone-400" />
                          ) : (
                            <Icons.Camera size={36} className="text-stone-400" />
                          )}
                        </div>
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                          <button onClick={() => handleViewEvidence(ev)} className="p-2 bg-white rounded-full hover:bg-stone-100">
                            <Icons.Eye size={14} />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Delete this evidence?')) {
                                deleteEvidenceMutation.mutate({ uuid: selectedViolation.uuid, evidenceId: ev._id });
                              }
                            }}
                            className="p-2 bg-white rounded-full hover:bg-stone-100"
                          >
                            <Icons.Trash size={14} className="text-red-600" />
                          </button>
                        </div>
                        <p className="text-xs text-stone-500 mt-1 truncate">{ev.filename}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-stone-50 rounded-xl border border-stone-100">
                    <Icons.Camera size={32} className="text-stone-300 mx-auto mb-2" />
                    <p className="text-sm text-stone-500">No evidence uploaded</p>
                  </div>
                )}
              </div>

              {/* Comments */}
              <div>
                <h4 className="text-sm font-semibold text-stone-700 mb-3">Comments ({comments.length})</h4>
                <div className="mb-4">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment…"
                    rows={3}
                  />
                  <div className="flex justify-end mt-2">
                    <Button
                      kind="primary"
                      size="sm"
                      disabled={!newComment.trim()}
                      onClick={() => {
                        if (newComment.trim()) {
                          addCommentMutation.mutate({
                            uuid: selectedViolation.uuid,
                            commentData: {
                              comment_text: newComment,
                              user_id: parseInt(localStorage.getItem('user_id') || '1'),
                              is_internal: false,
                            },
                          });
                        }
                      }}
                    >
                      Add Comment
                    </Button>
                  </div>
                </div>

                {comments.length > 0 ? (
                  <div className="space-y-2">
                    {comments.map((c) => (
                      <div key={c._id} className="bg-stone-50 rounded-xl border border-stone-100 p-3">
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center">
                              <Icons.User size={12} className="text-teal-600" />
                            </div>
                            <span className="text-sm font-medium text-stone-800">{c.user_name || 'User'}</span>
                          </div>
                          <span className="text-xs text-stone-400">
                            {format(new Date(c.created_at), 'MMM dd, yyyy HH:mm')}
                          </span>
                        </div>
                        <p className="text-sm text-stone-700 ml-9">{c.comment_text}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-sm text-stone-400 py-4">No comments yet</p>
                )}
              </div>
          </div>
        )}
        </DrawerBody>
      </Drawer>

      {/* ── Create Modal ───────────────────────────────────────────────────────── */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-stone-200">
            <div className="px-6 py-4 border-b border-stone-100 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-stone-900">Report New Violation</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors">
                <Icons.X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <Field label="Violation Type" required>
                <Input
                  value={newViolation.violation_type}
                  onChange={(e) => setNewViolation({ ...newViolation, violation_type: e.target.value })}
                  placeholder="e.g., Noise Complaint, Property Damage"
                />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Room ID">
                  <Input
                    type="number"
                    value={newViolation.room_id}
                    onChange={(e) => setNewViolation({ ...newViolation, room_id: e.target.value })}
                  />
                </Field>
                <Field label="Renter ID">
                  <Input
                    type="number"
                    value={newViolation.renter_id}
                    onChange={(e) => setNewViolation({ ...newViolation, renter_id: e.target.value })}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Severity">
                  <Select
                    value={newViolation.severity}
                    onChange={(e) => setNewViolation({ ...newViolation, severity: e.target.value })}
                    options={SEVERITY_OPTIONS.filter(o => o.value !== 'all')}
                  />
                </Field>
                <Field label="Occurred Date">
                  <Input
                    type="date"
                    value={newViolation.occurred_at}
                    onChange={(e) => setNewViolation({ ...newViolation, occurred_at: e.target.value })}
                  />
                </Field>
              </div>

              <Field label="Description" required>
                <Textarea
                  value={newViolation.description}
                  onChange={(e) => setNewViolation({ ...newViolation, description: e.target.value })}
                  rows={4}
                  placeholder="Describe the violation in detail…"
                />
              </Field>

              <div className="flex justify-end gap-3 pt-2 border-t border-stone-100">
                <Button kind="ghost" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button
                  kind="danger"
                  disabled={!newViolation.violation_type || !newViolation.description}
                  loading={createViolationMutation.isPending}
                  onClick={() => createViolationMutation.mutate(newViolation)}
                >
                  Report Violation
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Evidence Viewer ────────────────────────────────────────────────────── */}
      {viewingEvidence && (
        <div className="fixed inset-0 bg-stone-950/95 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl w-full">
            <button
              onClick={() => { URL.revokeObjectURL(viewingEvidence.url); setViewingEvidence(null); }}
              className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-stone-100 z-10 shadow-md"
            >
              <Icons.X size={18} />
            </button>
            {viewingEvidence.type === 'video' ? (
              <video src={viewingEvidence.url} controls className="w-full rounded-xl" />
            ) : (
              <img src={viewingEvidence.url} alt="Evidence" className="w-full rounded-xl" />
            )}
            <div className="mt-3 bg-white rounded-xl p-4 shadow-md">
              <p className="font-medium text-stone-900">{viewingEvidence.filename}</p>
              <p className="text-sm text-stone-500 mt-0.5">
                Uploaded on {format(new Date(viewingEvidence.upload_date), 'MMM dd, yyyy HH:mm')}
              </p>
              {viewingEvidence.description && (
                <p className="text-sm text-stone-600 mt-1">{viewingEvidence.description}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Violations;
